import type { Villager, VillagerSpecialty, VillageProfileType, ItemType, MarketPriceInfo } from './types'
import { SPECIALTY_GATHERS } from './types'

// Medieval name pools
const MALE_NAMES = [
  'Aldric','Beorn','Cedric','Dunstan','Edmund','Godwin','Harold','Leofric',
  'Oswald','Wulfric','Aelfric','Bran','Colm','Drystan','Emeric','Florian',
  'Gareth','Hadwin','Ivar','Kenric','Lothar','Magnus','Norbert','Osric',
  'Peregrine','Rolf','Sigmund','Torsten','Ulric','Wynstan',
]
const FEMALE_NAMES = [
  'Aelwen','Brynhild','Ceridwen','Drusilla','Elowen','Freya','Gwendolyn',
  'Helga','Isolde','Jocelyn','Kenna','Lyra','Merewen','Nessa','Olwen',
  'Rowena','Seren','Thora','Undine','Vivianne','Winifred','Yseult',
]

const SPECIALTY_WEIGHTS: Record<VillageProfileType, Partial<Record<VillagerSpecialty, number>>> = {
  farming:  { farmer: 4, shepherd: 2, lumberjack: 1, craftsman: 1 },
  mining:   { miner: 4, smith: 2, farmer: 1, craftsman: 1 },
  forestry: { lumberjack: 4, farmer: 1, craftsman: 2, shepherd: 1 },
  port:     { fisherman: 3, farmer: 2, craftsman: 2, smith: 1 },
  mountain: { miner: 3, smith: 2, lumberjack: 1, farmer: 1, craftsman: 1 },
}

const GATHER_RATES: Record<VillagerSpecialty, number> = {
  farmer: 2, shepherd: 1.5, miner: 1, lumberjack: 2.5,
  fisherman: 2, craftsman: 1.5, smith: 1,
}

function mulberry32(seed: number) {
  let s = seed | 0
  return () => {
    s = (s + 0x6D2B79F5) | 0
    let t = Math.imul(s ^ (s >>> 15), 1 | s)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

function pickWeighted(rng: () => number, weights: Partial<Record<VillagerSpecialty, number>>): VillagerSpecialty {
  const entries = Object.entries(weights) as Array<[VillagerSpecialty, number]>
  const total = entries.reduce((s, [, w]) => s + w, 0)
  let r = rng() * total
  for (const [spec, weight] of entries) {
    r -= weight
    if (r <= 0) return spec
  }
  return entries[0][0]
}

/**
 * Generate villagers for a village. When market prices are provided,
 * villager inventories scale based on pool supply levels — tying the
 * visual NPC economy to the server-side market state.
 */
export function generateVillagers(
  villageSeed: number,
  villageSize: 'small' | 'medium' | 'large',
  profileType: VillageProfileType,
  houseCount: number,
  marketPrices?: MarketPriceInfo[] | null,
): Villager[] {
  const rng = mulberry32(villageSeed * 9973 + 1337)
  const allNames = [...MALE_NAMES, ...FEMALE_NAMES]

  // Build a supply factor map from market data
  const supplyFactor = new Map<ItemType, number>()
  if (marketPrices) {
    for (const p of marketPrices) {
      // More supply in market → villagers have more gathered (they produced it)
      // Scale: 0.5x at scarce (reserve <30), 1x normal, 2x abundant (reserve >150)
      const ratio = Math.min(2, Math.max(0.3, p.pool_item_reserve / 100))
      supplyFactor.set(p.item, ratio)
    }
  }

  const extra = Math.floor(rng() * (villageSize === 'large' ? 9 : villageSize === 'medium' ? 7 : 5))
  const count = houseCount + extra

  const weights = SPECIALTY_WEIGHTS[profileType]
  const villagers: Villager[] = []
  const usedNames = new Set<string>()

  for (let i = 0; i < count; i++) {
    let name: string
    let attempts = 0
    do {
      name = allNames[Math.floor(rng() * allNames.length)]
      attempts++
    } while (usedNames.has(name) && attempts < 50)
    usedNames.add(name)

    const specialty = pickWeighted(rng, weights)
    const assignedHouseIndex = i < houseCount ? i : Math.floor(rng() * houseCount)

    // Inventory scales with market supply (villager production drives market)
    const inventory: Partial<Record<ItemType, number>> = {}
    const gatherItems = SPECIALTY_GATHERS[specialty]
    for (const item of gatherItems) {
      const baseQty = rng() * 8
      const factor = supplyFactor.get(item) ?? 1
      const qty = Math.floor(baseQty * factor)
      if (qty > 0) inventory[item] = qty
    }

    villagers.push({
      id: `v${villageSeed}-${i}`,
      name,
      specialty,
      assignedHouseIndex,
      inventory,
      gatherRate: GATHER_RATES[specialty],
      state: rng() < 0.6 ? 'gathering' : rng() < 0.8 ? 'idle' : 'trading',
    })
  }

  return villagers
}
